import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { useLanguage } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const { t, lang } = useLanguage();
  const suggestions = t("chat.suggestions");
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    setMessages((m) =>
      m.length === 0 || (m.length === 1 && m[0].role === "assistant")
        ? [{ role: "assistant", content: t("chat.greeting") }]
        : m
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6).trim();
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.token) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + parsed.token,
                };
                return copy;
              });
            } else if (parsed.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: parsed.error };
                return copy;
              });
            }
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t("chat.error") };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.8, type: "spring", stiffness: 260, damping: 20 }}
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[100] w-14 h-14 rounded-full bg-[#FF5A00] text-black flex items-center justify-center shadow-[0_0_40px_rgba(255,90,0,0.4)] hover:bg-[#FFB800] transition-colors duration-300"
        aria-label="Toggle chat"
        data-testid="chat-widget-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[100] w-[calc(100vw-3rem)] max-w-sm h-[520px] flex flex-col bg-black/60 backdrop-blur-xl border border-white/10 overflow-hidden"
            data-testid="chat-panel"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <img src="/fenrion-logo.png" alt="" className="w-9 h-9 object-contain mix-blend-screen" />
              <div>
                <p className="font-syne font-bold text-sm">{t("chat.title")}</p>
                <p className="text-xs text-neutral-500 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FFB800] animate-pulse" />
                  {t("chat.online")}
                </p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#FF5A00] text-black rounded-2xl rounded-br-sm"
                        : "bg-white/[0.06] border border-white/10 text-neutral-200 rounded-2xl rounded-bl-sm"
                    }`}
                    data-testid={`chat-message-${m.role}-${i}`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? "..." : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-xs border border-white/15 text-neutral-400 px-3 py-2 rounded-full hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors duration-300"
                      data-testid={`chat-suggestion-${suggestions.indexOf(s)}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 px-4 py-3 border-t border-white/10"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("chat.placeholder")}
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-neutral-600 py-2"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                className="w-9 h-9 rounded-full bg-[#FF5A00] text-black flex items-center justify-center hover:bg-[#FFB800] disabled:opacity-50 transition-colors duration-300"
                aria-label="Send message"
                data-testid="chat-send-button"
              >
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
